// src/hooks/useFeedFilters.ts
import {useMemo} from 'react';
import {FeedItem} from '../types/FeedItem';

type SortType = 'latest' | 'impact';

function toNumber(value: any): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeScoreToPct(value: any): number | null {
  const n = toNumber(value);
  if (n === null) return null;
  if (n <= 1) return Math.round(n * 100);
  return Math.round(Math.max(0, Math.min(100, n)));
}

function parseJsonLoose(value: any): any {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function extractImpactScore(provenance: string | Record<string, any> | undefined): number | null {
  if (!provenance) return null;
  try {
    const parsed = typeof provenance === 'string' ? JSON.parse(provenance) : provenance;
    const raw = String(parsed?.['영향도'] ?? '')
    .replace(/[*★()%]/g, '')
    .replace(/^예시:\s*/, '')
    .trim();
    const s = parseFloat(raw);
    return isNaN(s) ? null : s;
  } catch {
    return null;
  }
}

function getAudit(feed: any): any | null {
  const direct = feed?.mapping_audit ?? feed?.mappingAudit;
  if (direct && typeof direct === 'object') return direct;

  const metadata = parseJsonLoose(feed?.metadata);
  const metaAudit = metadata?.mapping_audit ?? metadata?.mappingAudit;
  if (metaAudit && typeof metaAudit === 'object') return metaAudit;

  if (feed?.final_audit_decision || feed?.audit_reason) {
    return {
      final_audit_decision: feed.final_audit_decision,
      audit_reason: feed.audit_reason,
      display_decision: feed.display_decision,
      mapping_strength: feed.mapping_strength,
      mapping_score: feed.mapping_score,
      ticker: feed.audit_ticker ?? feed.ticker,
      corp_name: feed.audit_corp_name ?? feed.corp_name,
    };
  }

  return null;
}

function getDisplayScore(feed: any): number {
  const audit = getAudit(feed);
  const auditDecision = String(audit?.final_audit_decision ?? audit?.finalAuditDecision ?? '').toLowerCase();

  if (auditDecision === 'no_actionable_mapping') return 0;

  const mappingScore = normalizeScoreToPct(
      audit?.mapping_score ??
      audit?.mappingScore ??
      audit?.relevance_score ??
      audit?.relevanceScore ??
      feed?.mapping_score ??
      feed?.mappingScore ??
      feed?.relevance_score ??
      feed?.relevanceScore,
  );

  if (mappingScore !== null) return mappingScore;

  return Number(feed?.impact_score ?? feed?.impactScore ?? extractImpactScore(feed?.provenance_reason) ?? 0);
}

function searchableBlob(feed: any): string {
  const audit = getAudit(feed);
  return JSON.stringify({
    title: feed?.title,
    title_ko: feed?.title_ko,
    summary: feed?.summary,
    summary_ko: feed?.summary_ko,
    corp_name: feed?.corp_name,
    ticker: feed?.ticker,
    direct_kor: feed?.direct_kor,
    related_stocks: feed?.related_stocks,
    mapping_audit: audit,
    provenance_reason: feed?.provenance_reason,
  }).toLowerCase();
}

export function useFeedFilters(
    feeds: FeedItem[] = [],
    options: {
      impactThreshold: number;
      dateRange: '1d' | '3d' | '7d' | 'all';
      stockSearch: string;
      searchTerm: string;
      sortType: SortType;
    },
) {
  const {impactThreshold, dateRange, stockSearch, searchTerm, sortType} = options;

  const displayedFeeds = useMemo(() => {
    let base = Array.isArray(feeds) ? [...feeds] : [];

    // 1) 영향도/매핑 점수 하한
    // - mapping_audit가 있으면 mapping score 우선
    // - 없으면 기존 impact_score 사용
    base = base.filter((f) => {
      if (!impactThreshold) return true;
      return getDisplayScore(f) >= impactThreshold;
    });

    // 2) 날짜
    if (dateRange !== 'all') {
      const days = dateRange === '1d' ? 1 : dateRange === '3d' ? 3 : 7;
      const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      base = base.filter((f) => {
        if (!f.published_at) return false;
        const dt = new Date(f.published_at);
        return !isNaN(dt.getTime()) && dt >= cutoff;
      });
    }

    // 3) 종목 키워드
    if (stockSearch.trim()) {
      const q = stockSearch.trim().toLowerCase();
      base = base.filter((f) => searchableBlob(f).includes(q));
    }

    // 4) 제목/요약 검색
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      base = base.filter((f) => {
        const text =
            (f.title || '') +
            (f.title_ko || '') +
            (f.summary || '') +
            (f.summary_ko || '') +
            ((f as any).audit_reason || '') +
            ((f as any).impact_reason || '');
        return text.toLowerCase().includes(q);
      });
    }

    // 5) 제목 기준 중복 제거
    const seen = new Set<string>();
    base = base.filter((f) => {
      const key = (f.title_ko || f.title || '').trim();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // 6) 정렬
    base.sort((a, b) =>
        sortType === 'impact'
            ? getDisplayScore(b) - getDisplayScore(a)
            : new Date(b.published_at ?? 0).getTime() - new Date(a.published_at ?? 0).getTime(),
    );

    return base;
  }, [feeds, impactThreshold, dateRange, stockSearch, searchTerm, sortType]);

  return displayedFeeds;
}
